import { useState } from "react";

import StrategyLocked from "@/components/strategy/StrategyLocked";
import StrategyLoading from "@/components/strategy/StrategyLoading";
import StrategyRoadmap from "@/components/strategy/StrategyRoadmap";
import StrategyWizard from "@/components/strategy/StrategyWizard";
import RecentStrategiesTray from "@/components/strategy/RecentStrategiesTray";
import TopNav from "@/components/smartpublish/TopNav";
import { useSession } from "@/contexts/SessionContext";

/**
 * Phase 5-A — /strategy page.
 *
 * Flow:
 *   1. Non-Creator/Pro tier → <StrategyLocked />
 *   2. Default → <StrategyWizard /> (5 steps)
 *   3. On "Generate" → <StrategyLoading /> while /api/strategy/generate runs
 *   4. Response → <StrategyRoadmap roadmap={...} />
 *   5. "Start new strategy" from the roadmap → back to the wizard
 */
export default function StrategyPage() {
    const { tier } = useSession();
    const [roadmap, setRoadmap] = useState(null);
    const [generating, setGenerating] = useState(false);

    const canUse = tier === "creator" || tier === "pro";

    const reset = () => {
        setRoadmap(null);
        setGenerating(false);
    };

    return (
        <div className="min-h-screen bg-[#0B0F19] text-[#E8ECF5]">
            <TopNav />

            {/* Subtle background orb */}
            <div
                className="pointer-events-none fixed inset-0 -z-10 opacity-60"
                aria-hidden
                style={{
                    background:
                        "radial-gradient(circle at 20% 10%, rgba(47,88,205,0.08) 0%, transparent 55%), radial-gradient(circle at 85% 90%, rgba(0,245,212,0.05) 0%, transparent 55%)",
                }}
            />

            <main
                className="mx-auto w-full max-w-[960px] px-6 pb-24 pt-10 sm:pt-14"
                data-testid="strategy-page"
            >
                <header className="mb-8 flex flex-col gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#00F5D4]">
                        strategy
                    </span>
                    <h1 className="text-3xl font-medium tracking-tight text-[#E8ECF5] sm:text-4xl">
                        Build a growth strategy that compounds.
                    </h1>
                    <p className="max-w-prose text-sm text-[#8A93A6]">
                        Pulse orchestrates a multi-platform roadmap —
                        day-by-day content hooks, titles, captions and formats
                        — tuned to your niche, audience and offer.
                    </p>
                </header>

                {!canUse && <StrategyLocked />}

                {canUse && !roadmap && !generating && (
                    <div className="flex flex-col gap-6">
                        <RecentStrategiesTray
                            onReplay={(savedRoadmap) => {
                                setRoadmap({
                                    ...savedRoadmap,
                                    // list balance_after as null since it was
                                    // already charged on the original run.
                                    balance_after: null,
                                });
                            }}
                        />
                        <StrategyWizard
                            onStartGenerating={() => setGenerating(true)}
                            onGenerated={(r) => {
                                setRoadmap(r);
                                setGenerating(false);
                            }}
                        />
                    </div>
                )}

                {canUse && generating && <StrategyLoading />}

                {canUse && roadmap && !generating && (
                    <StrategyRoadmap
                        roadmap={roadmap}
                        onStartNew={reset}
                    />
                )}
            </main>
        </div>
    );
}
